import { useTranslation } from 'react-i18next';
import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { ProposalTemplate } from '@modules/proposals/presentation/v2/pages/ProposalCreation/es/views/EditableTemplate/features/template';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import useGetBrokerage from '@modules/core/hooks/queries/useGetBrokerage';
import useGetBrokerProfile from '@modules/core/hooks/queries/useGetBrokerProfile';
import { useCreateProposalES } from '@modules/proposals/api/mutation/useCreateProposalES';
import { capturePdfFrameIntoHTML } from '@huspy/forge/shared';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';

const FILE_NAME = 'mortgage_proposal.pdf';
const DEFAULT_COLOR = '#000';
const DEFAULT_LOGO = 'https://huspy-production-public.s3.eu-central-1.amazonaws.com/static/Logo_bayteca_black.png';

export const EditableTemplate = () => {
  const { t } = useTranslation();
  const { location } = useRouterState();
  const navigate = useNavigate();

  const state = location.state as any;
  const validState = state?.firstName
      && state?.lastName
      && state?.offersCount;

  const clientDetails = {
    name: `${state.firstName} ${state.lastName}`,
    employmentStatus: state.employmentStatus,
    residencyStatus: state.residencyStatus,
  };
  const { user } = usePersistUser();
  const { data: brokerageData } = useGetBrokerage(user?.brokerageId);
  const { data: brokerData } = useGetBrokerProfile(user?.id);

  const [brokerDetails, setBrokerDetails] = useState({
    name: '',
    brokerageLogo: DEFAULT_LOGO,
    brokeragePrimaryColor: DEFAULT_COLOR,
    email: '',
    phone: '',
  });

  const proposalConfig = { numberOfOffers: state.offersCount };

  const { mutateAsync, isPending } = useCreateProposalES();
  const handleCreate = async () => {
    const html = await capturePdfFrameIntoHTML('proposal-template', FILE_NAME);
    await mutateAsync({
      number_of_offers: state.offersCount,
      applicants: [{ first_name: state.firstName, last_name: state.lastName }],
      proposal_html: html!,
    });
    navigate({ to: '/proposals' });
  };
  useEffect(() => {
    if (brokerageData) {
      setBrokerDetails((prevState) => ({
        ...prevState,
        name: brokerageData.name ?? '',
        brokeragePrimaryColor: brokerageData.primary_color ?? DEFAULT_COLOR,
        brokerageLogo: brokerageData.logo_url ?? prevState.brokerageLogo,
      }));
    }
  }, [brokerageData]);

  useEffect(() => {
    if (brokerData) {
      setBrokerDetails((prevState) => ({
        ...prevState,
        email: brokerData.email ?? '',
        phone: brokerData.phone_number ?? '',
      }));
    }
  }, [brokerData]);

  useEffect(() => {
    if (!validState) {
      navigate({ to: '/proposals' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validState]);

  useEffect(() => {
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_EDIT_STARTED, {
      useContext: true,
      addToContext: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex direction='column' gap='6'>
      <Flex direction='column'>
        <Flex>
          <Flex direction='column' gap='2'>
            <Text size='2xl' weight='medium' color='text-primary'>{t('proposals.proposalsCreate.title')}</Text>
            <Text size='md' weight='regular' color='text-secondary'>{t('proposals.proposalsCreate.subtitle')}</Text>
          </Flex>
          <Button
            variant={ isPending ? 'secondary' : 'primary' }
            type='button'
            loading={ isPending }
            ml='auto'
            onClick={ handleCreate }
          >
            {t('proposals.template.buttons.download')}
          </Button>
        </Flex>
      </Flex>
      <Flex id='proposal-template' direction='column' bg='white' pb='8'>
        <ProposalTemplate
          clientDetails={ clientDetails }
          brokerDetails={ brokerDetails }
          config={ proposalConfig }
        />
      </Flex>
    </Flex>
  );
};
